import {
  BaseEntity,
  ID,
  ListApiResponse,
  SingleApiResponse,
  unwrapApiErrorDetails
} from '@ianesthesie-kit/services';
import {
  BaseMedicalService,
  ListMedicalService,
  MedicalResponse,
  SingleMedicalService
} from '../baseMedical.service';
import {
  MedicalScore,
  MedicalScoreDescription,
  MedicalScoreName
} from './model';
import {
  captureThrowOnDev,
  GroupedList,
  GroupFunction,
  toKebabQuery
} from '@/util';
import { groupBy } from '@ianesthesie-kit/core';
import axios from 'axios';
import { Emphasis } from '@/modules/surgicalEvent';

export class MedicalScoreService extends BaseMedicalService<
  MedicalScore,
  boolean
> {
  private static INSTANCE_BY_ID: MedicalScoreService;
  private static POSTOP_ALLOWED_SCORES = [] as MedicalScoreName[];

  private constructor(patientId: string) {
    super('/score', patientId);
    MedicalScoreService.initPostopAllowedScores();
  }

  public static getInstance(patientId: ID): MedicalScoreService {
    if (this.shouldCreateNewInstance(this.INSTANCE_BY_ID, patientId)) {
      this.INSTANCE_BY_ID = new MedicalScoreService(patientId);
    }
    return this.INSTANCE_BY_ID;
  }

  public static fetchScoreNames(
    editable?: boolean,
    phases?: Array<'POSTOP' | 'PREOP' | 'ANY'>
  ): Promise<MedicalResponse<MedicalScoreName, true>> {
    const qs = toKebabQuery({
      editable,
      phase: phases
    });
    return axios
      .get<ListApiResponse<MedicalScoreName>>(`/bff/doctor/score${qs}`)
      .then((response) => ({
        data: response.data.data
      }))
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public static getPostopAllowedScores() {
    return Array.from(this.POSTOP_ALLOWED_SCORES) as Readonly<
      MedicalScoreName[]
    >;
  }

  public static isAllowedDuringPostop(scoreName?: MedicalScoreName) {
    return !!scoreName && this.POSTOP_ALLOWED_SCORES.includes(scoreName);
  }

  public static describe(
    name: MedicalScoreName
  ): Promise<MedicalResponse<MedicalScoreDescription, false>> {
    return axios
      .get<SingleApiResponse<MedicalScoreDescription>>(
        `/bff/doctor/score/${name}`
      )
      .then((response) => ({
        data: response.data.data
      }))
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public static lockedContextFieldOrCurrent<
    T extends BaseEntity,
    K extends keyof T
  >(
    key: K,
    currentEntity: T,
    score: MedicalScore | undefined
  ): T[K] | undefined {
    if (score?.locked) {
      return score.patientContextAtLockingTime?.[key as string];
    }

    return currentEntity?.[key];
  }

  public static getEmphasis(
    score: MedicalScoreName,
    value: number | undefined
  ): Emphasis {
    switch (score) {
      case MedicalScoreName.LEE:
        return this.emphasisForBounds(value, 2, 3);
      case MedicalScoreName.CARDIAC_RISK_FACTOR:
        return this.emphasisForBounds(value, 1, Number.MAX_SAFE_INTEGER);
      default:
        captureThrowOnDev(`Emphasis todo: ${score}`);
        return Emphasis.NOT_APPLICABLE;
    }
  }

  private static emphasisForBounds(
    val: number | undefined,
    informativeUpToExcl: number,
    importantUpToExcl: number
  ): Emphasis {
    if (val == null) {
      return Emphasis.NOT_APPLICABLE;
    }
    if (informativeUpToExcl > importantUpToExcl) {
      throw new Error(
        'INFORMATIVE upper bound cannot be greater than the IMPORTANT upper bound'
      );
    }
    if (val < informativeUpToExcl) {
      return Emphasis.INFORMATIVE;
    }
    if (val < importantUpToExcl) {
      return Emphasis.IMPORTANT;
    }

    return Emphasis.CRUCIAL;
  }

  private static initPostopAllowedScores() {
    const arr = this.POSTOP_ALLOWED_SCORES;
    if (!arr.length) {
      this.fetchScoreNames(true, ['POSTOP', 'ANY']).then(({ data }) => {
        arr.push(...data);
      });
    }
  }

  fetch(
    params: Record<string, any> = {}
  ): Promise<MedicalResponse<MedicalScore, true>> {
    return ListMedicalService.prototype.fetch.call(this, params);
  }

  fetchOne(
    entityId: ID,
    params: Record<string, any> = {}
  ): Promise<MedicalResponse<MedicalScore, false>> {
    return ListMedicalService.prototype.fetchOne.call(this, entityId, params);
  }

  public fetchGrouped<K extends string, L extends boolean>(
    params?: Record<string, any>
  ): Promise<{
    data: GroupedList<MedicalScore, K, L>;
  }> {
    return ListMedicalService.prototype.fetchGrouped.call(this, params);
  }

  upsert(
    item: Partial<MedicalScore>,
    params: Record<string, any>
  ): Promise<MedicalResponse<MedicalScore, false>> {
    return SingleMedicalService.prototype.upsert.call(this, item, params);
  }

  finalizeMultiple(
    payload: Partial<MedicalScore>[]
  ): Promise<MedicalResponse<void, false>> {
    const path = `${this.getUrl()}?finalize-multiple`;
    const transformedPayload = payload?.map((p) =>
      this.transformToRequestBody(p)
    );
    return axios
      .post(path, transformedPayload)
      .then(() => ({
        data: {} as any
      }))
      .catch((err) => {
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  delete(item: ID | MedicalScore): Promise<MedicalResponse<any, false>> {
    const itemId = typeof item === 'string' ? item : item.id;
    return axios
      .delete(this.getUrl(itemId))
      .then(() => ({ data: {} }))
      .catch((err) => {
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  protected transformResponseEntity(data: any): MedicalScore {
    if (!data) {
      return data;
    }
    const locked = !!data.lockedAt;
    return {
      ...data,
      locked,
      patientContextAtLockingTime: locked
        ? data.patientContextAtLockingTime ?? {}
        : data.patientContextAtLockingTime,
      payload: data.payload ?? {}
    };
  }

  protected transformToRequestBody(
    data: Partial<MedicalScore>
  ): Record<string, unknown> {
    return {
      scoreName: data.name,
      scorePayload: data.payload ?? {}
    };
  }

  protected getGroupingFunction<K extends string, L extends boolean>(): {
    apply: GroupFunction<MedicalScore, K, L>;
  } {
    return {
      apply: (data) => groupBy(data, 'name')
    };
  }
}
